import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/utils";

import { Alert as ShadAlert } from "./shadcn/alert";
import { Badge as ShadBadge } from "./shadcn/badge";
import { Button as ShadButton } from "./shadcn/button";
import { Card as ShadCard, CardContent as ShadCardContent, CardFooter as ShadCardFooter, CardHeader as ShadCardHeader } from "./shadcn/card";
import { Input as ShadInput } from "./shadcn/input";
import { Progress as ShadProgress } from "./shadcn/progress";
import { Table as ShadTable } from "./shadcn/table";
import { Textarea as ShadTextarea } from "./shadcn/textarea";

type TagProp = {
  // Keep legacy "tag" escape hatch without forcing strict typing
  tag?: any;
};

const buttonColors: Record<string, string> = {
  primary: "rounded-full border-transparent bg-[#2D8EE8] text-white hover:bg-[#1F7DD1] focus:ring-[#8EC3F6]",
  secondary: "rounded-full border-transparent bg-[#6B7892] text-white hover:bg-[#5B6780] focus:ring-[#B6C0CF]",
  success: "rounded-full border-transparent bg-[#6EDC2D] text-white hover:bg-[#60C521] focus:ring-[#B7EE95]",
  danger: "rounded-full border-transparent bg-[#FF4D4D] text-white hover:bg-[#E63B3B] focus:ring-[#FFB2B2]",
  warning: "rounded-full border-transparent bg-[#F2AF24] text-white hover:bg-[#DD9D18] focus:ring-[#F8D98C]",
  info: "rounded-full border-transparent bg-[#34B5E5] text-white hover:bg-[#249FCB] focus:ring-[#9EDCF2]",
  light: "rounded-full border-transparent bg-[#EEF2F6] text-slate-700 hover:bg-[#E0E7EE] focus:ring-[#D6DFE9]",
  dark: "rounded-full border-transparent bg-[#353153] text-white hover:bg-[#2A2744] focus:ring-[#A9A8B8]",
  link: "bg-transparent text-blue-600 hover:text-blue-700 underline focus:ring-blue-200",
  "soft-primary": "rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 focus:ring-blue-200",
  "soft-secondary": "rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-200",
  "soft-success": "rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 focus:ring-emerald-200",
  "soft-danger": "rounded-full bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-200",
  "soft-warning": "rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 focus:ring-amber-200",
  "soft-info": "rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 focus:ring-sky-200",
  "soft-dark": "rounded-full bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-300",
  "soft-light": "rounded-full bg-white text-slate-700 hover:bg-slate-100 focus:ring-slate-100",
};

const buttonOutlineColors: Record<string, string> = {
  primary: "rounded-full border border-[#2D8EE8] text-[#2D8EE8] hover:bg-[#E8F3FE] focus:ring-[#8EC3F6]",
  secondary: "rounded-full border border-[#6B7892] text-[#6B7892] hover:bg-[#EEF1F5] focus:ring-[#B6C0CF]",
  success: "rounded-full border border-[#60C521] text-[#60C521] hover:bg-[#EFFBE8] focus:ring-[#B7EE95]",
  danger: "rounded-full border border-[#FF4D4D] text-[#FF4D4D] hover:bg-[#FFEAEA] focus:ring-[#FFB2B2]",
  warning: "rounded-full border border-[#DD9D18] text-[#DD9D18] hover:bg-[#FFF6E3] focus:ring-[#F8D98C]",
  info: "rounded-full border border-[#249FCB] text-[#249FCB] hover:bg-[#EAF8FD] focus:ring-[#9EDCF2]",
  light: "rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-slate-100",
  dark: "rounded-full border border-[#353153] text-[#353153] hover:bg-[#F1F1F7] focus:ring-[#A9A8B8]",
  link: "text-blue-600 hover:text-blue-700 underline focus:ring-blue-200",
};

const buttonSizes: Record<string, string> = {
  sm: "px-2.5 py-1 text-xs",
  md: "px-3 py-2 text-sm",
  lg: "px-4 py-2.5 text-base",
};

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  color?: string;
  outline?: boolean;
  size?: "sm" | "md" | "lg";
  active?: boolean;
};

const splitColor = (color?: string) => {
  const parts = (color ?? "primary").split(" ").filter(Boolean);
  return {
    colorKey: parts[0] ?? "primary",
    extraClasses: parts.slice(1).join(" "),
  };
};

export function Button({
  color = "primary",
  outline = false,
  size = "md",
  className = "",
  active = false,
  type = "button",
  ...props
}: ButtonProps) {
  const { colorKey, extraClasses } = splitColor(color);
  const inferredOutline = outline || colorKey.startsWith("outline-");
  const baseColorKey = colorKey.replace(/^outline-/, "");

  const variant = (() => {
    if (inferredOutline) return "outline";
    if (baseColorKey === "danger") return "destructive";
    if (baseColorKey === "secondary") return "secondary";
    if (baseColorKey === "link") return "link";
    return "default";
  })() as React.ComponentProps<typeof ShadButton>["variant"];

  const sizeVariant = (() => {
    if (size === "sm") return "sm";
    if (size === "lg") return "lg";
    return "default";
  })() as React.ComponentProps<typeof ShadButton>["size"];

  const legacyColorClass = inferredOutline
    ? buttonOutlineColors[baseColorKey]
    : buttonColors[baseColorKey];

  return (
    <ShadButton
      type={type}
      variant={variant}
      size={sizeVariant}
      aria-pressed={active}
      className={cn(
        legacyColorClass,
        extraClasses,
        active ? "ring-2 ring-ring ring-offset-2" : "",
        className,
      )}
      {...props}
    />
  );
}

type ButtonGroupProps = {
  children: ReactNode;
  className?: string;
};

export function ButtonGroup({ children, className = "" }: ButtonGroupProps) {
  return <div className={`inline-flex items-center gap-1 ${className}`}>{children}</div>;
}

export function ButtonToolbar({ children, className = "" }: ButtonGroupProps) {
  return <div className={`flex flex-wrap items-center gap-2 ${className}`}>{children}</div>;
}

export function ButtonToggle({ active = false, ...props }: ButtonProps) {
  return <Button active={active} {...props} />;
}

type CardProps = {
  children: ReactNode;
  className?: string;
};

export function Card({ children, className = "" }: CardProps) {
  return <ShadCard className={cn("rounded-lg", className)}>{children}</ShadCard>;
}

export function CardHeader({ children, className = "" }: CardProps) {
  return <ShadCardHeader className={cn("border-b border-border px-4 py-3 font-semibold", className)}>{children}</ShadCardHeader>;
}

export function CardBody({ children, className = "" }: CardProps) {
  return <ShadCardContent className={cn("px-4 py-3", className)}>{children}</ShadCardContent>;
}

export function CardFooter({ children, className = "" }: CardProps) {
  return <ShadCardFooter className={cn("border-t border-border px-4 py-3", className)}>{children}</ShadCardFooter>;
}

type FormProps = React.FormHTMLAttributes<HTMLFormElement>;

export function Form({ children, className = "", ...props }: FormProps) {
  return (
    <form className={className} {...props}>
      {children}
    </form>
  );
}

type FormGroupProps = {
  children: ReactNode;
  className?: string;
};

export function FormGroup({ children, className = "" }: FormGroupProps) {
  return <div className={`mb-3 ${className}`}>{children}</div>;
}

type FormFeedbackProps = {
  children: ReactNode;
  className?: string;
  type?: "valid" | "invalid";
};

export function FormFeedback({ children, className = "", type = "invalid" }: FormFeedbackProps) {
  const colorClass = type === "valid" ? "text-emerald-600" : "text-red-600";
  return <div className={`mt-1 text-sm ${colorClass} ${className}`}>{children}</div>;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  bsSize?: "sm" | "lg";
  invalid?: boolean;
};

export function Input({
  className = "",
  bsSize,
  invalid = false,
  type = "text",
  ...props
}: InputProps) {
  const sizeClass = bsSize === "sm" ? "px-2 py-1 text-sm" : bsSize === "lg" ? "px-4 py-2 text-base" : "px-3 py-2 text-sm";
  const borderClass = invalid
    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
    : "border-slate-300 focus:border-blue-500 focus:ring-blue-200";

  const commonClass = cn(
    "w-full",
    sizeClass,
    invalid ? "ring-2 ring-red-200" : "",
    borderClass,
    className,
  );

  if (type === "textarea") {
    const { children, ...rest } = props as any;
    return (
      <ShadTextarea className={commonClass} {...rest}>
        {children}
      </ShadTextarea>
    );
  }

  if (type === "select") {
    const { children, ...rest } = props as any;
    return (
      <select
        className={cn(
          "w-full rounded-md border bg-background text-foreground shadow-sm focus:outline-none focus:ring-2",
          commonClass,
        )}
        {...rest}
      >
        {children}
      </select>
    );
  }

  if (type === "checkbox" || type === "radio") {
    return (
      <input
        type={type}
        className={cn(
          "size-4 border-slate-300 text-blue-600 focus:ring-blue-500",
          type === "checkbox" ? "rounded" : "rounded-full",
          className
        )}
        {...props}
      />
    );
  }

  return <ShadInput type={type} className={commonClass} {...props} />;
}

type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;

export function Label({ children, className = "", ...props }: LabelProps) {
  return (
    <label className={`block text-sm font-medium text-slate-700 ${className}`} {...props}>
      {children}
    </label>
  );
}

type TableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  responsive?: boolean;
};

export function Table({ children, className = "", responsive = false, ...props }: TableProps) {
  const table = (
    <ShadTable className={cn("w-full text-sm", className)} {...props}>
      {children}
    </ShadTable>
  );

  if (responsive) {
    return <div className="w-full overflow-x-auto">{table}</div>;
  }

  return table;
}

type NavProps = {
  children: ReactNode;
  className?: string;
};

export function Nav({ children, className = "" }: NavProps) {
  return <ul className={`flex flex-wrap items-center gap-2 ${className}`}>{children}</ul>;
}

export function NavItem({ children, className = "" }: NavProps) {
  return <li className={`nav-item ${className}`}>{children}</li>;
}

type NavLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & TagProp & {
  active?: boolean;
};

export function NavLink({ children, className = "", active = false, tag, ...props }: NavLinkProps) {
  const Tag: any = tag ?? "a";
  return (
    <Tag className={`nav-link ${active ? "active" : ""} ${className}`} {...props}>
      {children}
    </Tag>
  );
}

type TabContentProps = {
  children: ReactNode;
  activeTab?: string;
  className?: string;
};

type TabPaneProps = {
  children: ReactNode;
  tabId?: string;
  className?: string;
  activeTab?: string;
};

export function TabContent({ children, activeTab, className = "" }: TabContentProps) {
  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<TabPaneProps>, { activeTab });
      })}
    </div>
  );
}

export function TabPane({ children, tabId, className = "", activeTab }: TabPaneProps) {
  const isActive = activeTab === undefined || activeTab === tabId;
  return <div className={`${isActive ? "block" : "hidden"} ${className}`}>{children}</div>;
}

type ListGroupProps = {
  children: ReactNode;
  className?: string;
  flush?: boolean;
};

export function ListGroup({ children, className = "", flush = false }: ListGroupProps) {
  return <ul className={`list-group ${flush ? "list-group-flush" : ""} ${className}`}>{children}</ul>;
}

type ListGroupItemProps = React.HTMLAttributes<HTMLLIElement> & TagProp & {
  active?: boolean;
  disabled?: boolean;
};

export function ListGroupItem({ children, className = "", active = false, disabled = false, tag, ...props }: ListGroupItemProps) {
  const Tag: any = tag ?? "li";
  return (
    <Tag
      className={`list-group-item ${active ? "active" : ""} ${disabled ? "disabled" : ""} ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

type SpinnerProps = {
  className?: string;
  color?: "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";
  size?: "sm" | "md" | "lg";
};

const spinnerSizes: Record<string, string> = {
  sm: "h-4 w-4 border-2",
  md: "h-6 w-6 border-2",
  lg: "h-8 w-8 border-4",
};

const spinnerColors: Record<string, string> = {
  primary: "border-blue-600",
  secondary: "border-slate-500",
  success: "border-emerald-600",
  danger: "border-red-600",
  warning: "border-amber-500",
  info: "border-sky-600",
  light: "border-slate-200",
  dark: "border-slate-900",
};

export function Spinner({ className = "", color = "primary", size = "md" }: SpinnerProps) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-r-transparent ${spinnerSizes[size] ?? spinnerSizes.md} ${spinnerColors[color] ?? spinnerColors.primary} ${className}`}
    />
  );
}

type BadgeProps = {
  children: ReactNode;
  className?: string;
  color?: string;
  pill?: boolean;
};

export function Badge({ children, className = "", color = "primary", pill = false }: BadgeProps) {
  const { colorKey, extraClasses } = splitColor(color);
  const inferredOutline = colorKey.startsWith("outline-");
  const baseColorKey = colorKey.replace(/^outline-/, "");
  const variant = (() => {
    if (inferredOutline) return "outline";
    if (baseColorKey === "danger") return "destructive";
    if (baseColorKey === "secondary") return "secondary";
    return "default";
  })() as React.ComponentProps<typeof ShadBadge>["variant"];

  const colorClass = inferredOutline
    ? buttonOutlineColors[baseColorKey]
    : buttonColors[baseColorKey];
  return (
    <ShadBadge
      variant={variant}
      className={cn(
        pill ? "rounded-full" : "",
        colorClass,
        extraClasses,
        className,
      )}
    >
      {children}
    </ShadBadge>
  );
}

type AlertProps = {
  children: ReactNode;
  className?: string;
  color?: string;
};

const alertColors: Record<string, string> = {
  primary: "bg-blue-50 text-blue-700 border-blue-200",
  secondary: "bg-slate-50 text-slate-700 border-slate-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  light: "bg-slate-50 text-slate-700 border-slate-200",
  dark: "bg-slate-800 text-white border-slate-700",
};

export function Alert({ children, className = "", color = "primary" }: AlertProps) {
  const { colorKey, extraClasses } = splitColor(color);
  const variant = (colorKey === "danger" ? "destructive" : "default") as React.ComponentProps<typeof ShadAlert>["variant"];
  return (
    <ShadAlert
      variant={variant}
      className={cn(
        "px-4 py-3",
        alertColors[colorKey] ?? alertColors.primary,
        extraClasses,
        className,
      )}
    >
      {children}
    </ShadAlert>
  );
}

type ProgressProps = {
  value?: number;
  max?: number;
  color?: string;
  className?: string;
  barClassName?: string;
};

const progressColors: Record<string, string> = {
  primary: "bg-blue-600",
  secondary: "bg-slate-500",
  success: "bg-emerald-600",
  danger: "bg-red-600",
  warning: "bg-amber-500",
  info: "bg-sky-600",
  light: "bg-slate-200",
  dark: "bg-slate-800",
};

export function Progress({ value = 0, max = 100, color = "primary", className = "", barClassName = "" }: ProgressProps) {
  const { colorKey } = splitColor(color);
  const percentage = Math.min(100, Math.max(0, (Number(value) / Math.max(1, Number(max))) * 100));

  if (colorKey === "primary" && barClassName === "") {
    return <ShadProgress value={percentage} className={className} />;
  }

  return (
    <div className={`h-2 w-full overflow-hidden rounded bg-slate-200 ${className}`}>
      <div
        className={`h-full ${progressColors[colorKey] ?? progressColors.primary} ${barClassName}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

type TooltipProps = {
  isOpen?: boolean;
  target?: string;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  children: ReactNode;
};

export function Tooltip({
  isOpen = false,
  target,
  placement = "top",
  className = "",
  children,
}: TooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (!isOpen || !target) return;
    const targetEl = document.getElementById(target);
    if (!targetEl || !tooltipRef.current) return;

    const targetRect = targetEl.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;

    let top = targetRect.top;
    let left = targetRect.left;

    switch (placement) {
      case "bottom":
        top = targetRect.bottom + gap;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.right + gap;
        break;
      case "top":
      default:
        top = targetRect.top - tooltipRect.height - gap;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
    }

    setStyle({
      position: "fixed",
      top: Math.max(8, top),
      left: Math.max(8, left),
      zIndex: 60,
    });
  }, [isOpen, target, placement, children]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const handle = () => {
      if (!target) return;
      const targetEl = document.getElementById(target);
      if (!targetEl || !tooltipRef.current) return;
      const targetRect = targetEl.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const gap = 8;

      let top = targetRect.top;
      let left = targetRect.left;

      switch (placement) {
        case "bottom":
          top = targetRect.bottom + gap;
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
          break;
        case "left":
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
          left = targetRect.left - tooltipRect.width - gap;
          break;
        case "right":
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
          left = targetRect.right + gap;
          break;
        case "top":
        default:
          top = targetRect.top - tooltipRect.height - gap;
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
          break;
      }

      setStyle({
        position: "fixed",
        top: Math.max(8, top),
        left: Math.max(8, left),
        zIndex: 60,
      });
    };

    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen, target, placement]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow ${className}`}
      style={style}
      role="tooltip"
    >
      {children}
    </div>,
    document.body
  );
}

type NavbarTogglerProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export function NavbarToggler({ className = "", type = "button", ...props }: NavbarTogglerProps) {
  return (
    <button type={type} className={`navbar-toggler ${className}`} {...props} />
  );
}

type CarouselProps = {
  activeIndex?: number;
  next?: () => void;
  previous?: () => void;
  interval?: number;
  enableTouch?: boolean;
  className?: string;
  children: ReactNode;
  style?: React.CSSProperties;
};

export function Carousel({ activeIndex = 0, className = "", children, style }: CarouselProps) {
  const items = React.Children.toArray(children);
  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {items.map((child, index) => (
        <div key={index} className={index === activeIndex ? "block" : "hidden"}>
          {child}
        </div>
      ))}
    </div>
  );
}

type CarouselItemProps = {
  children: ReactNode;
  className?: string;
  onExiting?: () => void;
  onExited?: () => void;
};

export function CarouselItem({ children, className = "" }: CarouselItemProps) {
  return <div className={`relative ${className}`}>{children}</div>;
}

type CarouselIndicatorsProps = {
  items: Array<{ key?: number | string }>;
  activeIndex: number;
  onClickHandler: (index: number) => void;
};

export function CarouselIndicators({ items, activeIndex, onClickHandler }: CarouselIndicatorsProps) {
  return (
    <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-2">
      {items.map((_, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onClickHandler(index)}
          className={`h-2 w-2 rounded-full ${activeIndex === index ? "bg-white" : "bg-white/50"}`}
          aria-label={`Slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

type CarouselControlProps = {
  direction: "prev" | "next";
  directionText?: string;
  onClickHandler: () => void;
};

export function CarouselControl({ direction, directionText, onClickHandler }: CarouselControlProps) {
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      onClick={onClickHandler}
      className={`absolute top-1/2 -translate-y-1/2 ${isPrev ? "left-2" : "right-2"} rounded-full bg-black/40 px-2 py-1 text-white`}
      aria-label={directionText ?? direction}
    >
      {isPrev ? "‹" : "›"}
    </button>
  );
}

type CarouselCaptionProps = {
  children: ReactNode;
  className?: string;
};

export function CarouselCaption({ children, className = "" }: CarouselCaptionProps) {
  return <div className={`carousel-caption ${className}`}>{children}</div>;
}

type UncontrolledCarouselItem = {
  src: string;
  altText?: string;
  caption?: string;
  header?: string;
  key?: number | string;
};

type UncontrolledCarouselProps = {
  items: UncontrolledCarouselItem[];
  className?: string;
};

export function UncontrolledCarousel({ items, className = "" }: UncontrolledCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!items || items.length === 0) return null;

  const next = () => setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  const previous = () => setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));

  return (
    <div className={`relative ${className}`}>
      <Carousel activeIndex={activeIndex}>
        {items.map((item) => (
          <CarouselItem key={item.key ?? item.src}>
            <img src={item.src} alt={item.altText ?? ""} className="block w-full object-cover" />
            {(item.header || item.caption) && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-4 py-2 text-white">
                {item.header && <h5 className="text-sm font-semibold">{item.header}</h5>}
                {item.caption && <p className="text-xs">{item.caption}</p>}
              </div>
            )}
          </CarouselItem>
        ))}
      </Carousel>
      {items.length > 1 && (
        <>
          <CarouselIndicators items={items} activeIndex={activeIndex} onClickHandler={setActiveIndex} />
          <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </>
      )}
    </div>
  );
}
